<script setup lang="ts">
import { useToast } from "vue-toastification";
import {deepClone, type ObjectId, type Tag, TagNamespace} from "~/composables/charon";
import { FetchError } from "ofetch";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";

const { t, locale, locales } = useI18n()
const { charon } = useCharon(true)
const toast = useToast()

const input = ref<HTMLInputElement | null>(null)

const props = defineProps<{
  tag: Partial<Tag>
  namespace: TagNamespace
  open: boolean
}>()

const emit = defineEmits<{
  (e: "created", value: ObjectId): void
  (e: "updated", value: ObjectId): void
  (e: "deleted", value: ObjectId): void
  (e: "cancel", value: void): void
}>()

const tag = ref<Tag | null>({
  id: null,
  namespace: props.namespace,
  locales: {},
  ...deepClone(props.tag),
})

const selectableLocales = computed(() => Object.keys(tag.value?.locales ?? []))

const selectedLocale = ref<string|null>(anyLocale())

const pending = ref(false)

watch([() => props.tag, () => props.open], () => {
  if (!props.open) {
    return
  }

  input.value?.focus()

  tag.value = {
    id: null,
    namespace: props.namespace,
    locales: {},
    ...deepClone(props.tag),
  }

  selectedLocale.value = anyLocale()
})

function anyLocale(): string | null {
  return selectableLocales.value.find(l => l == locale.value)
    ?? selectableLocales.value.find(_ => true)
    ?? null
}

function addLocale(locale: string) {
  tag.value!!.locales[locale] = {
    label: "",
    description: "",
  }

  selectedLocale.value = locale
}

function removeLocale(locale: string) {
  tag.value!!.locales = Object.fromEntries(
    Object.entries(tag.value!!.locales)
      .filter(([l, _]) => l !== locale)
  )

  if (selectedLocale.value == locale) {
    selectedLocale.value = anyLocale()
  }
}

function submit() {
  if (tag.value!.id) {
    updateTag()
  } else {
    createTag()
  }
}

function createTag() {
  pending.value = true
  charon<Tag>(`/tags`, { method: 'PUT', body: tag.value })
    .then(created => {
      toast.success(t('jc.component.tagEditor.toast.tagCreated', { tag: created }))
      emit("created", created.id!!)
    })
    .catch((reason: FetchError) => {
      toast.error(t(`jc.component.tagEditor.toast.tagCreateFailed.${reason.statusCode || 'fallback'}`))
    })
    .finally(() => {
      pending.value = false
    })
}

function updateTag() {
  pending.value = true
  charon<Tag>(`/tags`, { method: 'PATCH', body: tag.value })
    .then(updated => {
      toast.success(t('jc.component.tagEditor.toast.tagUpdated', { tag: updated }))
      emit("updated", updated.id!!)
    })
    .catch((reason: FetchError) => {
      toast.error(t(`jc.component.tagEditor.toast.tagUpdateFailed.${reason.statusCode || 'fallback'}`))
    })
    .finally(() => {
      pending.value = false
    })
}

async function deleteTag() {
  pending.value = true
  charon<Tag>(`/tags/${tag.value!.id!!}`, { method: 'DELETE' })
    .then(() =>{
      toast.success(t('jc.component.tagEditor.toast.tagDeleted'))
      emit("deleted", tag.value!.id!!)
    })
    .finally(() => {
      pending.value = false
    })
}

function cancel() {
  emit("cancel")
}
</script>

<template>
  <Modal
    :open="open"
    @cancel="cancel"
  >
    <ModalHeader>
      <div class="flex flex-row gap-2">
        <h3 class="mr-auto my-auto">
          {{ t('jc.component.tagEditor.title') }}
        </h3>
        <LocaleSelector
          :locales="locales"
          :selectable="selectableLocales"
          v-model:selected="selectedLocale"
          @click:add="addLocale"
          @click:remove="removeLocale"
        />
      </div>
    </ModalHeader>

    <ModalContent v-if="tag">
      <span
        class="hidden data-[invalid=true]:inline-block mt-2 text-xs text-red-500"
        :data-invalid="!selectedLocale"
      >
        {{ t('jc.component.tagEditor.none') }}
      </span>
      <template v-if="selectedLocale">
        <label class="mt-4 w-full flex flex-col justify-center">
          <span class="text-xs">{{ t('jc.component.tagEditor.labelInput.label') }}</span>
          <input
            ref="input"
            :placeholder="t('jc.component.tagEditor.labelInput.placeholder')"
            type="text"
            v-model="tag.locales[selectedLocale!!].label"
            class="mt-1 block rounded text-gray-900 invalid:[&:not(:focus)]:border-red-500 peer"
            pattern="[a-zA-Z0-9\-_\s]*"
          >
          <span class="mt-2 hidden text-xs text-red-500 peer-[&:not(:focus):invalid]:block">
          {{ t('jc.component.tagEditor.labelInput.invalid') }}
        </span>
        </label>
        <label class="mt-4 w-full flex flex-col justify-center">
          <span class="text-xs">{{ t('jc.component.tagEditor.descriptionInput.label') }}</span>
          <input
            :placeholder="t('jc.component.tagEditor.descriptionInput.placeholder')"
            type="text"
            v-model="tag.locales[selectedLocale!!].description"
            class="mt-1 block rounded text-gray-900 invalid:[&:not(:focus)]:border-red-500 peer"
            pattern=".*"
          >
          <span class="mt-2 hidden text-xs text-red-500 peer-[&:not(:focus):invalid]:block">
          {{ t('jc.component.tagEditor.descriptionInput.invalid') }}
        </span>
        </label>
      </template>
    </ModalContent>

    <ModalActions v-if="tag">
      <button
        type="submit"
        :disabled="pending || Object.keys(tag.locales).length == 0"
        @click="submit"
        :title="t('jc.component.tagEditor.submitButton.title')"
        class="inline-flex gap-2 w-full justify-center rounded bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-highlighted active:bg-primary-selected disabled:opacity-50 mobile:ml-3 mobile:w-auto"
      >
        <font-awesome-icon
          fixed-width
          :spin="pending"
          :icon="pending ? 'spinner-third' : 'save'"
          size="lg"
        />
        {{ t('jc.component.tagEditor.submitButton.label') }}
      </button>
      <button
        type="button"
        :disabled="pending || !tag.id"
        @click="deleteTag"
        :title="t('jc.component.tagEditor.deleteButton.title')"
        class="inline-flex gap-2 w-full justify-center rounded bg-danger px-3 py-2 text-sm font-semibold text-white hover:bg-danger-highlighted active:bg-danger-selected disabled:opacity-50 mobile:ml-3 mobile:w-auto"
      >
        <font-awesome-icon fixed-width icon="trash" />
        {{ t('jc.component.tagEditor.deleteButton.label') }}
      </button>
    </ModalActions>
  </Modal>
</template>
